import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CUSTOM_ERROR_HANDLERS,
  CUSTOM_HTTP_ERROR_HANDLER_PRIORITY,
  CustomHttpErrorHandlerService,
} from '@abp/ng.theme.shared';
import { MODAL, ModalService } from '../modals/modal.service';

const DEFAULT_TITLE = 'Une erreur est survenue';
const DEFAULT_MESSAGE = 'Le service n’a pas pu traiter votre demande. Merci de réessayer dans quelques instants.';

interface AbpErrorEnvelope {
  message?: string;
  details?: string;
}

/**
 * Rend les erreurs HTTP d'ABP dans la popup du site plutôt que dans les siennes.
 *
 * ABP affiche ses propres modales et pages d'erreur, stylées par le thème LeptonX. Le site client
 * ne charge plus ce thème — ses bundles CSS sont déclarés `inject: false` et n'étaient injectés
 * que par son initialiseur — si bien que ces écrans apparaissent sans mise en forme, par-dessus la
 * page. Le site a déjà son propre système de modales, utilisé partout ailleurs : on y renvoie.
 *
 * Le 401 reste volontairement à ABP : la perte de session est déjà traitée par l'intercepteur HTTP
 * et l'`AuthGuardService`, et détourner ce cas ici risquerait de faire disparaître l'annonce sans
 * rien mettre à la place.
 */
@Injectable({ providedIn: 'root' })
export class AbpErrorPopupHandler implements CustomHttpErrorHandlerService {
  /** Au-dessus des gestionnaires d'ABP, qui plafonnent à `high`. */
  readonly priority = CUSTOM_HTTP_ERROR_HANDLER_PRIORITY.veryHigh;

  private readonly modalService = inject(ModalService);
  private error?: HttpErrorResponse;

  canHandle(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status === 401) {
      return false;
    }

    this.error = error;
    return true;
  }

  execute(): void {
    const error = this.error;
    this.error = undefined;

    // Une panne fait souvent échouer plusieurs appels d'une même page, et une popup déjà ouverte
    // peut porter une annonce plus précise (session expirée, service indisponible) : on ne
    // l'écrase jamais.
    if (!error || this.modalService.currentId !== MODAL.NONE) {
      return;
    }

    const envelope = (error.error as { error?: AbpErrorEnvelope } | null)?.error;
    const hasDetails = Boolean(envelope?.details);

    this.modalService.display(MODAL.GENERIC_POPUP, {
      // Le titre est interpolé, donc déjà échappé par Angular : l'échapper ici l'afficherait
      // encodé (« n&#39;est »). Seul le message passe par `[innerHTML]`.
      title: hasDetails ? envelope!.message || DEFAULT_TITLE : DEFAULT_TITLE,
      message: escapeHtml(
        (hasDetails ? envelope!.details : envelope?.message) || DEFAULT_MESSAGE,
      ),
      validateBtn: 'Fermer',
    });
  }
}

/**
 * `GenericPopupComponent` rend son message via `[innerHTML]` et un pipe qui contourne la
 * désinfection d'Angular. Le texte vient ici d'une réponse serveur : on l'échappe donc avant de le
 * lui confier, sans quoi une chaîne relayée depuis un service amont pourrait injecter du balisage.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export const abpErrorPopupProvider = {
  provide: CUSTOM_ERROR_HANDLERS,
  multi: true,
  useExisting: AbpErrorPopupHandler,
};
